import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import useScrollReveal from "../useScrollReveal";
import useSectionAnalytics, { mergeRefs } from "../useSectionAnalytics";
import { useTranslation } from "react-i18next";

function CountUp({ end, suffix = "", duration = 2.5, visible }) {
  const ref = useRef(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!visible || hasRun.current || !ref.current) return;
    hasRun.current = true;

    const obj = { val: 0 };
    const endNum = parseFloat(end);

    gsap.to(obj, {
      val: endNum,
      duration,
      ease: "power3.out",
      onUpdate: () => {
        if (ref.current) {
          ref.current.textContent = Math.floor(obj.val) + suffix;
        }
      },
    });
  }, [visible, end, suffix, duration]);

  return <span ref={ref}>0{suffix}</span>;
}

export default function Stats() {
  const [sectionRef, visible] = useScrollReveal();
  const analyticsRef = useSectionAnalytics("stats");
  const lineRefs = useRef([]);
  const cardRefs = useRef([]);
  const { t } = useTranslation();

  const stats = [
    {
      num: "2000", suffix: "+",
      label: t("stats.labels.patients"),
      sub: t("stats.subs.patients"),
      isStatic: false,
    },
    {
      num: "10", suffix: "+",
      label: t("stats.labels.corporate"),
      sub: t("stats.subs.corporate"),
      isStatic: false,
    },
    {
      num: "08:00 - 20:00", suffix: "",
      label: t("stats.labels.hours"),
      sub: t("stats.subs.hours"),
      isStatic: true,
    },
    {
      num: "30", suffix: "%",
      label: t("stats.labels.reduced"),
      sub: t("stats.subs.reduced"),
      isStatic: false,
    },
  ];

  // Animate divider lines on visible
  useEffect(() => {
    if (!visible) return;
    lineRefs.current.forEach((line, i) => {
      if (!line) return;
      gsap.fromTo(
        line,
        { scaleY: 0, transformOrigin: "top center" },
        { scaleY: 1, duration: 0.8, ease: "power2.out", delay: i * 0.1 + 0.3 }
      );
    });
  }, [visible]);

  // Stagger the stat cards themselves in, instead of the whole section
  // fading in as one flat block. clearProps hands control of `transform`
  // back to CSS once the tween finishes, so the hover-lift rule can apply.
  useEffect(() => {
    if (!visible) return;
    gsap.fromTo(
      cardRefs.current,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.12,
        onComplete: () => gsap.set(cardRefs.current, { clearProps: "opacity,transform" }),
      }
    );
  }, [visible]);

  return (
    <section ref={mergeRefs(sectionRef, analyticsRef)} className={`stats ${visible ? "visible" : ""}`}>

      {/* Section tag */}
      <p className="stats__tag">
        {t("stats.tag")}
      </p>

      <h2>{t("stats.title")}</h2>

      <div className="stats-grid">
        {stats.map((stat, i) => (
          <div
            key={i}
            ref={(el) => (cardRefs.current[i] = el)}
            className="stat-card"
          >

            {/* Vertical divider */}
            {i > 0 && (
              <div
                ref={(el) => (lineRefs.current[i - 1] = el)}
                className="stat-divider"
              />
            )}

            {/* Number */}
            <div className="stat-card__number">
              {stat.isStatic ? (
                <span className="stat-card__static">{stat.num}</span>
              ) : visible ? (
                <CountUp
                  end={stat.num}
                  suffix={stat.suffix}
                  duration={2.5}
                  visible={visible}
                />
              ) : (
                <span>0{stat.suffix}</span>
              )}
            </div>

            {/* Label */}
            <p className="stat-card__label">{stat.label}</p>

            {/* Sub */}
            <p className="stat-card__sub">{stat.sub}</p>

          </div>
        ))}
      </div>

    </section>
  );
}
